import { Component, OnChanges, SimpleChanges, input, output } from '@angular/core';
import { TaskQuestionCorrectPercent } from '@shared/model/task.models';
import { TimeSpanPipe } from '@shared/pipes/time-span.pipe';
import { getEnvaluateColor } from '@shared/utils/utils';
import { flagBase } from '../task-analysis-chart/flag';
import { isNil } from 'es-toolkit';

/** 进入图表前补齐展示所需字段的输入结构。 */
interface TaskQuestionCorrectPercentWithClassroomName extends TaskQuestionCorrectPercent {
  classroomName: string;
  finishCount?: number;
}

/** 构建图表 ViewModel 时使用的输入参数。 */
interface ColumnChartViewModelInput {
  questionsStatis: TaskQuestionCorrectPercentWithClassroomName[];
  compareQuestionsStatis: TaskQuestionCorrectPercentWithClassroomName[];
  correctFilter: number | null | undefined;
  currentQuestionNo: string;
  isGroup: boolean;
}

/** X 轴题号按钮的展示元数据。 */
interface ColumnAxisMeta {
  no: string;
  preferred: boolean;
  discussCount: number;
  active: boolean;
  width: number;
}

/** 单根柱子的展示元数据。 */
interface ColumnBarMeta {
  no: string;
  classroomName: string;
  correctPercent: number | null;
  incorrectPercent: number | null;
  correctColor: string;
  finishCount?: number;
  wrongStudentCount?: number;
  unansweredCount?: number;
  preferred?: boolean;
  discussCount?: number;
  raw: TaskQuestionCorrectPercentWithClassroomName | null;
}

/** 单个题号分组，单班 1 根柱，对比模式多根柱。 */
interface ColumnGroupMeta {
  no: string;
  axis: ColumnAxisMeta;
  bars: ColumnBarMeta[];
}

/** 模板直接消费的图表 ViewModel。 */
interface ColumnChartViewModel {
  columnGroups: ColumnGroupMeta[];
  chartMinWidth: number;
  classrooms: string[];
  groupMinWidth: number;
}

@Component({
  selector: 'fl-task-analysis-column-div-chart',
  templateUrl: './task-analysis-column-div-chart.component.html',
  styleUrls: ['./task-analysis-column-div-chart.component.scss'],
  standalone: false,
})
export class TaskAnalysisColumnDivChartComponent implements OnChanges {
  readonly questionsStatis = input<TaskQuestionCorrectPercentWithClassroomName[]>([]);
  readonly compareQuestionsStatis = input<TaskQuestionCorrectPercentWithClassroomName[]>([]);
  readonly currentQuestionNo = input<string>(undefined);
  readonly correctFilter = input<number>(undefined);
  readonly isGroup = input(false);
  readonly studentCount = input<number>(undefined);
  readonly changeNo = output<TaskQuestionCorrectPercentWithClassroomName>();

  readonly flagBase = flagBase;
  readonly yAxisTicks = [100, 80, 60, 40, 20, 0];
  readonly barHeight = 180;
  readonly minBarWidth = 30;
  readonly barGap = 24;
  readonly groupGap = 24;
  readonly chartTrackColor = '#EDF2FA';
  private readonly timeSpanPipe = new TimeSpanPipe();

  columnGroups: ColumnGroupMeta[] = [];
  chartMinWidth = 100;
  classrooms: string[] = [];
  groupMinWidth = 24;
  gridTemplateColumns = '';
  tooltipVisible = false;
  tooltipLines: string[] = [];
  tooltipLeft = 0;
  tooltipTop = 0;

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes['questionsStatis'] ||
      changes['compareQuestionsStatis'] ||
      changes['currentQuestionNo'] ||
      changes['correctFilter'] ||
      changes['isGroup']
    ) {
      this.rebuildViewModel();
    }
  }

  /** 题号分组 trackBy，避免模板重复销毁 / 重建 DOM。 */
  trackGroup(_: number, group: ColumnGroupMeta) {
    return group.no;
  }

  /** 单个柱子 trackBy。题号 + 班级名可以唯一定位一根柱子。 */
  trackBar(_: number, bar: ColumnBarMeta) {
    return `${bar.no}-${bar.classroomName || 'default'}`;
  }

  showBarTooltip(event: MouseEvent, bar: ColumnBarMeta) {
    this.tooltipLines = this.getBarTooltipLines(bar);
    this.tooltipVisible = true;
    this.updateTooltipPosition(event);
  }

  hideBarTooltip() {
    this.tooltipVisible = false;
  }

  moveBarTooltip(event: MouseEvent) {
    if (!this.tooltipVisible) {
      return;
    }

    this.updateTooltipPosition(event);
  }

  /** 点击题号按钮时切题，回传该题号下第一条可用原始数据。 */
  emitByAxis(group: ColumnGroupMeta) {
    const raw = group.bars.find(bar => bar.raw)?.raw;
    if (raw) {
      this.changeNo.emit(raw);
    }
  }

  /** 点击单根柱子时，直接回传对应原始数据。 */
  emitByBar(bar: ColumnBarMeta) {
    if (bar.raw) {
      this.changeNo.emit(bar.raw);
    }
  }

  /** 柱子的 tooltip 文案，沿用原题目图的展示口径。 */
  getBarTooltip(bar: ColumnBarMeta): string {
    if (!bar.raw || bar.correctPercent === null) {
      return `题号: ${bar.no}\n该班级无此题数据`;
    }

    if (this.isGroup()) {
      const lines = [`题号: ${bar.no}`, `班级: ${bar.classroomName}`, `得分率: ${bar.correctPercent ?? 0}%`];
      this.appendMedianDurationLine(lines, bar);
      return lines.join('\n');
    }

    const lines = [
      `题号: ${bar.no}`,
      `得分率: ${bar.correctPercent ?? 0}%`,
      `完成人数: ${bar.finishCount ?? 0}`,
      `答错人数: ${bar.wrongStudentCount ?? 0}`,
      `未作答人数: ${bar.unansweredCount ?? 0}`,
    ];
    this.appendMedianDurationLine(lines, bar);
    return lines.join('\n');
  }

  /** 中位用时存在且大于 0 时追加到 tooltip 最后一行。 */
  private appendMedianDurationLine(lines: string[], bar: ColumnBarMeta) {
    const medianDuration = bar.raw?.medianDuration;
    if (medianDuration === undefined || medianDuration === null || medianDuration <= 0) {
      return;
    }
    lines.push(`作答时间: ${this.timeSpanPipe.formatSecondText(medianDuration)}`);
  }

  /** 自定义 tooltip 的分行内容，和原 ECharts tooltip 保持一致。 */
  getBarTooltipLines(bar: ColumnBarMeta): string[] {
    return this.getBarTooltip(bar).split('\n');
  }

  private updateTooltipPosition(event: MouseEvent) {
    const offset = 12;
    const tooltipWidth = 160;
    const tooltipHeight = Math.max(this.tooltipLines.length * 20 + 16, 40);
    const maxLeft = window.innerWidth - tooltipWidth - 8;
    const preferredTop = event.clientY - tooltipHeight - offset;

    this.tooltipLeft = Math.min(Math.max(event.clientX + offset, 8), maxLeft);
    this.tooltipTop = preferredTop > 8 ? preferredTop : event.clientY + offset;
  }

  /** 柱体过矮时不显示内部百分比文案。 */
  showBarLabel(bar: ColumnBarMeta): boolean {
    return bar.correctPercent !== null && bar.correctPercent >= 12;
  }

  /** 题号按钮宽度不能小于所在柱组宽度。 */
  getAxisWidth(group: ColumnGroupMeta): number {
    return Math.max(group.axis.width, this.groupMinWidth);
  }

  /** 单班模式下，有完成人数数据时展示题目完成占比饼图。 */
  shouldShowUnansweredProgress(bar: ColumnBarMeta): boolean {
    if (bar.unansweredCount === 0 || isNil(bar.unansweredCount)) return false;
    return (
      !this.isGroup() &&
      !!bar.raw &&
      bar.finishCount !== undefined &&
      bar.finishCount !== null &&
      this.studentCount() > 0
    );
  }

  /** 完成标记用饼图展示，扇形占比代表完成人数。 */
  getUnansweredProgressBackground(bar: ColumnBarMeta): string {
    const finishCount = bar.finishCount ?? 0;
    const finishPercent = this.clampPercent((finishCount / (finishCount + bar.unansweredCount)) * 100);
    const degree = (finishPercent / 100) * 360;
    const color = this.getUnansweredProgressColor(bar);

    return `conic-gradient(${color} 0deg ${degree}deg, transparent ${degree}deg 360deg)`;
  }

  /** 完成标记颜色使用完成率对应的色卡颜色。 */
  getUnansweredProgressColor(bar: ColumnBarMeta): string {
    const finishCount = bar.finishCount ?? 0;
    const finishPercent = this.clampPercent((finishCount / (finishCount + bar.unansweredCount)) * 100);

    return getEnvaluateColor(finishPercent);
  }

  /** 重建图表视图状态，并同步派生字段。 */
  private rebuildViewModel() {
    const viewModel = this.buildColumnChartViewModel({
      questionsStatis: this.questionsStatis(),
      compareQuestionsStatis: this.compareQuestionsStatis(),
      currentQuestionNo: this.currentQuestionNo(),
      correctFilter: this.correctFilter(),
      isGroup: this.isGroup(),
    });

    this.columnGroups = viewModel.columnGroups;
    this.chartMinWidth = viewModel.chartMinWidth;
    this.classrooms = viewModel.classrooms;
    this.groupMinWidth = viewModel.groupMinWidth;
    this.gridTemplateColumns = this.columnGroups.length
      ? `repeat(${this.columnGroups.length}, minmax(${this.groupMinWidth}px, 1fr))`
      : '';
  }

  /** 把外部输入映射为图表专用 ViewModel。 */
  private buildColumnChartViewModel(input: ColumnChartViewModelInput): ColumnChartViewModel {
    // 用 Map 按“题号 + 班级”去重。
    const dataMap = new Map<string, TaskQuestionCorrectPercentWithClassroomName>();

    [...(input.questionsStatis || []), ...(input.compareQuestionsStatis || [])].forEach(item => {
      const key = `${item.no}-${item.classroomName}`;
      if (!dataMap.has(key)) {
        dataMap.set(key, { ...item });
      }
    });

    let processedData = Array.from(dataMap.values());

    // 得分率筛选：保留 <= 阈值的题目。
    if (input.correctFilter !== undefined && input.correctFilter !== null) {
      processedData = processedData.filter(item => (item.correctPercent ?? 0) <= input.correctFilter);
    }

    // 提取当前参与绘制的班级和题号。
    const classrooms = Array.from(new Set(processedData.map(item => item.classroomName))).filter(
      classroom => !!classroom?.trim()
    );
    const uniqueQuestionNos = Array.from(new Set(processedData.map(item => item.no)));

    // 建立“班级 -> 题号”的查找表。
    const dataLookup: Record<string, Record<string, TaskQuestionCorrectPercentWithClassroomName>> = {};
    processedData.forEach(item => {
      if (!dataLookup[item.classroomName]) {
        dataLookup[item.classroomName] = {};
      }
      dataLookup[item.classroomName][item.no] = item;
    });

    // 单班模式绘制一组柱子；对比模式按全部班级并排展示。
    const effectiveClassrooms = input.isGroup ? classrooms : [classrooms[0] || ''];

    // 计算每个题号分组的最小宽度。
    const barsPerGroup = Math.max(effectiveClassrooms.length, 1);
    const groupMinWidth = barsPerGroup * this.minBarWidth + (barsPerGroup - 1) * this.barGap;

    // 逐题构建分组，每个 group 下再构建班级柱子列表。
    const columnGroups = uniqueQuestionNos.map(no => {
      const questionData = processedData.find(item => item.no === no);
      const discussCount = questionData?.discussCount || 0;

      // X 轴题号按钮展示信息。
      const axis: ColumnAxisMeta = {
        no,
        preferred: questionData?.preferred || false,
        discussCount,
        active: input.currentQuestionNo === no,
        width: discussCount > 0 ? 60 : 40,
      };

      // 每个题号下，按班级生成柱子。
      const bars = effectiveClassrooms.map(classroomName => {
        const raw = input.isGroup ? dataLookup[classroomName]?.[no] || null : questionData || null;
        const correctPercent = raw?.correctPercent ?? null;

        return {
          no,
          classroomName,
          correctPercent,
          incorrectPercent: correctPercent === null ? null : Math.max(0, 100 - correctPercent),
          correctColor: correctPercent === null ? 'transparent' : getEnvaluateColor(correctPercent),
          finishCount: raw?.finishCount,
          wrongStudentCount: raw?.wrongStudentCount,
          unansweredCount: raw?.unansweredCount,
          preferred: raw?.preferred,
          discussCount: raw?.discussCount,
          raw,
        } as ColumnBarMeta;
      });

      return {
        no,
        axis,
        bars,
      } as ColumnGroupMeta;
    });

    // 估算整张图表的最小宽度，放不下时由外层横向滚动承接。
    const totalBars = uniqueQuestionNos.length * (input.isGroup ? classrooms.length : 1);
    const chartMinWidth = Math.max(
      uniqueQuestionNos.length * groupMinWidth + Math.max(uniqueQuestionNos.length - 1, 0) * this.groupGap + 8,
      totalBars * this.minBarWidth,
      100
    );

    return {
      columnGroups,
      chartMinWidth,
      classrooms,
      groupMinWidth,
    };
  }

  private clampPercent(value: number): number {
    return Math.min(Math.max(value, 0), 100);
  }
}
